/**
 * Auditor API Service
 * 
 * Handles auditor invitation preview and acceptance flows.
 * Part of Sprint 4 auditor SSO implementation.
 */

import { getBaseUrl } from '../utils/apiConfig';

/**
 * Auditor invitation preview response
 */
export interface AuditorInvitationPreview {
  invitation_id: string;
  audited_tenant_name: string;
  audited_tenant_domain: string;
  scope_frameworks: string[];
  audit_window_start: string;
  audit_window_end: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

/**
 * Auditor accept response (JWT tokens + dashboard URL)
 */
export interface AuditorAcceptResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  dashboard_url: string;
}

/**
 * Auditor API service class
 */
class AuditorApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${getBaseUrl()}/api/v1/auditor`;
  }

  /**
   * Preview an auditor invitation (public endpoint)
   * 
   * @param token - Invitation token from email link
   * @returns Invitation preview with redacted details
   */
  async previewInvitation(token: string): Promise<AuditorInvitationPreview> {
    const response = await fetch(`${this.baseUrl}/invitation/preview?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to preview invitation' }));
      throw new Error(error.detail || `Preview failed: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Accept an auditor invitation after SAML authentication
   * 
   * This endpoint is called from the SAML callback handler.
   * It validates the invitation token and SAML assertion email match,
   * creates the auditor user, and returns JWT tokens.
   * 
   * @param token - Invitation token from email link
   * @param samlSessionId - SAML session ID from IdP callback
   * @returns JWT tokens and dashboard URL
   */
  async acceptInvitation(token: string, samlSessionId: string): Promise<AuditorAcceptResponse> {
    const response = await fetch(`${this.baseUrl}/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        saml_session_id: samlSessionId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to accept invitation' }));
      throw new Error(error.detail || `Accept failed: ${response.status}`);
    }

    return await response.json();
  }
}

// Export singleton instance
export const auditorApi = new AuditorApiService();

// Export class for testing
export { AuditorApiService };
